// @repo/observability/lazy — opt-in deferred loading for heavy observability
// SDKs. Calls made before a bundle lands are queued and flushed in order once
// it does; apps that keep the eager factories are unaffected.

import { createErrorReporter, type ErrorReporter } from './errors'

export interface LazySink<T> {
  /** Run `fn` now if the sink has loaded, otherwise queue it for the in-order flush. */
  run(fn: (sink: T) => void): void
  /** Start loading (once). `configure` runs against the sink before the queue flushes. */
  load(configure?: (sink: T) => void): void
}

export function createLazySink<T>(loader: () => Promise<T>): LazySink<T> {
  let sink: T | null = null
  let loadStarted = false
  let loadFailed = false
  const pending: Array<(sink: T) => void> = []

  return {
    run(fn) {
      if (sink) fn(sink)
      else if (!loadFailed) pending.push(fn)
    },
    load(configure) {
      if (loadStarted) return
      loadStarted = true
      void loader()
        .then(loaded => {
          configure?.(loaded)
          sink = loaded
          for (const fn of pending.splice(0)) fn(loaded)
        })
        .catch((error: unknown) => {
          // Chunk load failed (flaky network, deploy skew): drop the queue and
          // stop accepting calls so `pending` doesn't grow unboundedly.
          loadFailed = true
          pending.length = 0
          console.error('[observability] lazy sink failed to load', error)
        })
    }
  }
}

/**
 * Lazy PostHog facade. `withPostHog` no-ops entirely when `enabled` is false;
 * `lazyInitPostHog` starts the dynamic import and runs the app's
 * `posthog.init` configuration before flushing queued calls.
 */
export function createLazyPostHog<T>({
  load,
  enabled
}: {
  load: () => Promise<T>
  enabled: boolean
}): {
  withPostHog: (fn: (posthog: T) => void) => void
  lazyInitPostHog: (configure: (posthog: T) => void) => void
} {
  const sink = createLazySink(load)

  return {
    withPostHog(fn) {
      if (enabled) sink.run(fn)
    },
    lazyInitPostHog(configure) {
      if (enabled) sink.load(configure)
    }
  }
}

/**
 * {@link ErrorReporter} whose backing sink (e.g. Sentry) loads on first use.
 *
 * Pass `eager: true` to start loading immediately instead of on first use.
 * Use this server-side: queued captures flush inside the loader's `.then`,
 * which runs under the async context (and therefore the Sentry request scope)
 * of whichever request triggered the load — loading at module evaluation
 * closes that cross-request misattribution window.
 */
export function createLazyErrorReporter(
  load: () => Promise<ErrorReporter>,
  { eager = false }: { eager?: boolean } = {}
): ErrorReporter {
  const sink = createLazySink(load)
  if (eager) sink.load()
  const withSink = (fn: (sink: ErrorReporter) => void) => {
    sink.run(fn)
    sink.load()
  }

  return createErrorReporter({
    captureException: (error, extra) => withSink(s => s.captureException(error, extra)),
    captureMessage: (message, extra) => withSink(s => s.captureMessage(message, extra))
  })
}

/**
 * Run `fn` once the window has loaded and the main thread is idle, so deferred
 * SDK downloads never compete with initial paint or hydration. Browser-only.
 */
export function scheduleIdle(fn: () => void): void {
  const runWhenIdle = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 5000 })
    } else {
      setTimeout(fn, 1)
    }
  }
  if (document.readyState === 'complete') {
    runWhenIdle()
  } else {
    window.addEventListener('load', runWhenIdle, { once: true })
  }
}
